import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Envelope, ShieldCheck } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-md w-full border border-border bg-white p-8">
          <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-3">Forgot password</div>
          <h1 className="font-heading text-3xl font-bold mb-2">Reset your password</h1>
          <p className="text-sm text-muted-foreground mb-8">Enter your account email and we'll send a link to set a new password. The link expires in 1 hour.</p>

          {submitted ? (
            <div className="border border-border bg-muted/30 p-5" data-testid="forgot-submitted">
              <ShieldCheck size={22} weight="duotone" className="text-primary mb-2" />
              <p className="text-sm leading-relaxed">
                If an account exists for <b>{email}</b>, we've sent a reset link to that address. Check your inbox (and spam folder) in the next few minutes.
              </p>
              <div className="mt-5 flex gap-3">
                <Link to="/login">
                  <Button variant="outline" className="rounded-sm" data-testid="forgot-back-to-login">Back to sign in</Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="forgot-email" className="text-xs">Email</Label>
                <div className="relative mt-1.5">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-9 rounded-sm h-11"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 rounded-sm" data-testid="forgot-submit-btn">
                {busy ? "Sending link…" : "Send reset link"}
              </Button>
              <p className="text-sm text-muted-foreground text-center pt-2">
                Remembered it?{" "}
                <Link to="/login" className="underline font-medium text-foreground">Back to sign in</Link>
              </p>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
